import { useEffect, useState } from "react";
import {
  Outlet,
  Link,
  NavLink,
} from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/client";
import "./Layout.css";

const LOGO =
  process.env.REACT_APP_LOGO || "/logo.png";

const isVideo = /\.(mp4|webm)$/i.test(LOGO);

const WHATSAPP_NUMBER = "919362280474";

const WHATSAPP_MESSAGE =
  "Hello NovaStore Support, I need help with my order.";

const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  WHATSAPP_MESSAGE
)}`;

export default function Layout() {
  const { user, logout } = useAuth();
  const [topGames, setTopGames] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api
      .get("/product/all")
      .then((response) => {
        setTopGames(
          (response.data.products || []).slice(0, 4)
        );
      })
      .catch(() => {});
  }, []);

  return (
    <div className="shell">
      <a href="#main" className="sr-only">
        Skip to content
      </a>

      <header className="hdr">
        <div className="hdr-in">
          <button
            type="button"
            className="hdr-menu"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
            aria-expanded={menuOpen}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
              <line x1="4" y1="7" x2="20" y2="7" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="17" x2="20" y2="17" />
            </svg>
          </button>

          <Link to="/" className="logo">
            {isVideo ? (
              <video
                src={LOGO}
                autoPlay
                muted
                loop
                playsInline
                className="logo-media"
              />
            ) : (
              <img
                src={LOGO}
                alt="NovaStore"
                className="logo-media"
              />
            )}
          </Link>

          <div className="hdr-spacer" />

          <nav className="nav-desktop">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                isActive ? "on" : ""
              }
            >
              Home
            </NavLink>

            <NavLink
              to="/orders"
              className={({ isActive }) =>
                isActive ? "on" : ""
              }
            >
              Orders
            </NavLink>

            <NavLink
              to="/support"
              className={({ isActive }) =>
                isActive ? "on" : ""
              }
            >
              Help
            </NavLink>
          </nav>

          <div className="hdr-right">
            {user ? (
              <>
                <Link
                  to="/wallet"
                  className="balance tabular"
                >
                  ₹{Number(user.balance).toFixed(2)}
                </Link>

                <Link
                  to="/profile"
                  className="linkbtn"
                >
                  Profile
                </Link>

                {user.isAdmin && (
                  <Link
                    to="/admin"
                    className="linkbtn"
                  >
                    Admin
                  </Link>
                )}

                <button
                  type="button"
                  className="linkbtn"
                  onClick={logout}
                >
                  Sign out
                </button>

                <button
                  type="button"
                  className="logout-icon"
                  onClick={logout}
                  title="Sign out"
                  aria-label="Sign out"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line
                      x1="21"
                      y1="12"
                      x2="9"
                      y2="12"
                    />
                  </svg>
                </button>
              </>
            ) : (
              <Link to="/login" className="login-pill">
                LOGIN
              </Link>
            )}
          </div>
        </div>

        {menuOpen && (
          <div className="hdr-drawer">
            <Link to="/" onClick={() => setMenuOpen(false)}>Home</Link>
            <Link to="/orders" onClick={() => setMenuOpen(false)}>Orders</Link>
            <Link to="/wallet" onClick={() => setMenuOpen(false)}>Wallet</Link>
            <Link to="/profile" onClick={() => setMenuOpen(false)}>Profile</Link>
            {user && user.isAdmin && (
              <Link to="/admin" onClick={() => setMenuOpen(false)}>Admin</Link>
            )}
            <Link to="/leaderboard" onClick={() => setMenuOpen(false)}>Leaderboard</Link>
            <Link to="/support" onClick={() => setMenuOpen(false)}>Help Center</Link>
            <Link to="/about" onClick={() => setMenuOpen(false)}>About Us</Link>
            <Link to="/faq" onClick={() => setMenuOpen(false)}>FAQ</Link>
          </div>
        )}
      </header>

      <main id="main">
        <Outlet />
      </main>

      {/* Floating WhatsApp customer support */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="support-chat-float"
        aria-label="Chat with NovaStore support on WhatsApp"
        title="Chat with NovaStore support"
      >
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>

        <span>24/7 Chat</span>
      </a>

      <nav className="btm-nav">
        <NavLink
          to="/"
          end
          className={({ isActive }) =>
            `btm-tab${isActive ? " on" : ""}`
          }
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>

          <span>Home</span>
        </NavLink>

        <NavLink
          to="/orders"
          className={({ isActive }) =>
            `btm-tab${isActive ? " on" : ""}`
          }
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect
              x="2"
              y="3"
              width="20"
              height="14"
              rx="2"
            />
            <path d="M8 21h8" />
            <path d="M12 17v4" />
          </svg>

          <span>Orders</span>
        </NavLink>

        <NavLink
          to="/wallet"
          className={({ isActive }) =>
            `btm-tab${isActive ? " on" : ""}`
          }
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
            <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
            <path d="M18 12a2 2 0 0 0 0 4h4v-4z" />
          </svg>

          <span>Wallet</span>
        </NavLink>

        <NavLink
          to="/leaderboard"
          className={({ isActive }) =>
            `btm-tab${isActive ? " on" : ""}`
          }
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5C7 4 7 7 7 7" />
            <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5C17 4 17 7 17 7" />
            <path d="M4 22h16" />
            <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22" />
            <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22" />
            <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
          </svg>

          <span>Top 10</span>
        </NavLink>

        {user && user.isAdmin ? (
          <NavLink
            to="/admin"
            className={({ isActive }) =>
              `btm-tab${isActive ? " on" : ""}`
            }
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />

              <circle
                cx="12"
                cy="12"
                r="3"
              />
            </svg>

            <span>Admin</span>
          </NavLink>
        ) : user ? (
          <NavLink
            to="/support"
            className={({ isActive }) =>
              `btm-tab${isActive ? " on" : ""}`
            }
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
              />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line
                x1="12"
                y1="17"
                x2="12.01"
                y2="17"
              />
            </svg>

            <span>Help</span>
          </NavLink>
        ) : (
          <NavLink
            to="/login"
            className={({ isActive }) =>
              `btm-tab${isActive ? " on" : ""}`
            }
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle
                cx="12"
                cy="7"
                r="4"
              />
            </svg>

            <span>Sign in</span>
          </NavLink>
        )}
      </nav>

      <footer className="ftr">
        <div className="ftr-grid">
          <div className="ftr-col ftr-about">
            <Link
              to="/"
              className="ftr-logo"
            >
              {isVideo ? (
                <video
                  src={LOGO}
                  autoPlay
                  muted
                  loop
                  playsInline
                  style={{
                    height: 40,
                    borderRadius: 8,
                  }}
                />
              ) : (
                <img
                  src={LOGO}
                  alt="NovaStore"
                  style={{
                    height: 40,
                    borderRadius: 8,
                  }}
                />
              )}
            </Link>

            <p>
              NovaStore is your one-stop destination
              for instant game top-ups. Access your
              favourite games anytime, anywhere,
              with secure payments and instant
              delivery.
            </p>
          </div>

          <div className="ftr-col">
            <h4>Hot Games</h4>

            <ul>
              {topGames.map((game) => (
                <li key={game.id}>
                  <Link to={`/game/${game.id}`}>
                    <img
                      src={`/${game.image}`}
                      alt=""
                      className="ftr-game-img"
                      onError={(event) => {
                        event.currentTarget.style.display =
                          "none";
                      }}
                    />

                    <span>{game.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="ftr-col">
            <h4>Useful Links</h4>

            <ul>
              <li>
                <Link to="/about">About Us</Link>
              </li>

              <li>
                <Link to="/privacy">
                  Privacy Policy
                </Link>
              </li>

              <li>
                <Link to="/terms">
                  Terms &amp; Conditions
                </Link>
              </li>

              <li>
                <Link to="/support">
                  Help Center
                </Link>
              </li>

              <li>
                <Link to="/faq">
                  General FAQs
                </Link>
              </li>
            </ul>
          </div>

          <div className="ftr-col">
            <h4>Contact</h4>

            <ul className="ftr-contact">
              <li>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <rect
                    x="2"
                    y="4"
                    width="20"
                    height="16"
                    rx="2"
                  />

                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>

                <a href="mailto:support@novaofficial.in">
                  support@novaofficial.in
                </a>
              </li>

              <li>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                  <circle
                    cx="12"
                    cy="10"
                    r="3"
                  />
                </svg>

                <span>Manipur, India</span>
              </li>
            </ul>

            <div className="ftr-social">
              <a
                href="https://www.instagram.com/novaofficial.in/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect
                    x="2"
                    y="2"
                    width="20"
                    height="20"
                    rx="5"
                  />

                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />

                  <line
                    x1="17.5"
                    y1="6.5"
                    x2="17.51"
                    y2="6.5"
                  />
                </svg>
              </a>

              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />

                  <path d="m10 15 5-3-5-3z" />
                </svg>
              </a>

              <a
                href="https://telegram.me/Chandrathokchom"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Telegram"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="m22 2-7 20-4-9-9-4Z" />
                  <path d="M22 2 11 13" />
                </svg>
              </a>

              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp Support"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" />

                  <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="ftr-bottom">
          © {new Date().getFullYear()} NovaStore —
          All Rights Reserved 🎮
        </div>
      </footer>
    </div>
  );
}
